/**
 * ACC-030: the customer edits intake answers after onboarding. A change that affects the formula
 * is sent to a physician.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { IntakeAnswers, QuestionId } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { loadAccountIntake, saveAccountIntake } from "../api.ts";
import { QuestionBody } from "../start/QuestionBody.tsx";
import { answerLine, PROMPT, visibleQuestions } from "../start/questions.ts";
import { AccountShell } from "./Shell.tsx";
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "list";
  readonly answers: IntakeAnswers;
  readonly notice: "review" | "saved" | null;
} | {
  readonly kind: "edit";
  readonly answers: IntakeAnswers;
  readonly id: QuestionId;
};
export function AccountIntake() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const load = () => {
    setScreen({
      kind: "loading"
    });
    void loadAccountIntake().then(loaded => {
      if (loaded === "signed_out") setScreen({
        kind: "signed_out"
      });else if (loaded === null) setScreen({
        kind: "failed"
      });else setScreen({
        kind: "list",
        answers: loaded,
        notice: null
      });
    }, () => setScreen({
      kind: "failed"
    }));
  };
  useEffect(() => {
    document.title = "Your health answers";
    load();
  }, []);
  if (screen.kind === "loading") return <AccountShell title="Your health answers">
        <Pending message="Loading your answers" bones={["title", "line", "row", "row"]} />
      </AccountShell>;
  if (screen.kind === "signed_out") return <AccountShell title="Your health answers" signedOut>
        {null}
      </AccountShell>;
  if (screen.kind === "failed") return <AccountShell title="Your health answers">
        <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Try again in a minute. If it keeps happening, write to support.
        </p>
        <Button onClick={load}>Try again</Button>
      </AccountShell>;
  if (screen.kind === "edit") {
    const prompt = PROMPT[screen.id];
    const commit = async (id: QuestionId, answer: NonNullable<IntakeAnswers[QuestionId]>) => {
      if (saving) return;
      setSaving(true);
      setFailed(false);
      const result = await saveAccountIntake(id, answer);
      setSaving(false);
      if (result === "failed") {
        setFailed(true);
        return;
      }
      setScreen({
        kind: "list",
        answers: {
          ...screen.answers,
          [id]: answer
        },
        notice: result === "review" ? "review" : "saved"
      });
    };
    return <AccountShell title="Your health answers">
        <div className="x78zum5 xdt5ytf x15agyyk" data-testid="account-intake-edit">
          <p id="question" className="x1ghz6dp x1jty25b x1fzhlzt">
            {prompt.heading}
          </p>
          <p className="x1ghz6dp x1jty25b x1fzhlzt">{prompt.lead}</p>
          {saving && <Status>Saving your answer</Status>}
          {failed && <Status state="problem">
              We couldn't save that. Check your connection and try again.
            </Status>}
          <QuestionBody labelledBy="question" id={screen.id} answers={screen.answers} onAnswer={(id, answer) => void commit(id, answer)} />
          <div className="x78zum5 x626il7 x1a02dak">
            <Button tone="quiet" size="small" onClick={() => setScreen({
            kind: "list",
            answers: screen.answers,
            notice: null
          })}>
              Cancel
            </Button>
          </div>
        </div>
      </AccountShell>;
  }
  const vis = visibleQuestions(screen.answers);
  return <AccountShell title="Your health answers">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">
        Change an answer any time. A change that affects your formula goes to a physician.
      </p>
      {screen.notice === "review" && <Status state="ok" testId="intake-review">
          A physician will review this change. Your current formula keeps shipping until they sign a
          new one.
        </Status>}
      {screen.notice === "saved" && <Status state="ok">Saved.</Status>}
      <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" data-testid="account-intake">
        {vis.map(id => <li key={id} data-testid={`intake-row-${id}`} className="xrvj5dj xju1xpo x1vdzsm xcfm6fe x6s0dn4 x1wiri7k xez8t42 x13fuv20 x178xt8z">
            <div>
              <p className="x1ghz6dp xl4dl4e x1jty25b">{PROMPT[id].heading}</p>
              <p className="x1ghz6dp xqu410z x1fzhlzt">{answerLine(id, screen.answers)}</p>
            </div>
            <Button tone="quiet" size="small" testId={`edit-${id}`} onClick={() => setScreen({
          kind: "edit",
          answers: screen.answers,
          id
        })}>
              Change
            </Button>
          </li>)}
      </ul>
    </AccountShell>;
}